"use client"

import { useState } from "react"
import { CheckCircle, AlertCircle, X, ChevronDown, ChevronUp } from "lucide-react"

// Toast notification component
export const Toast = ({ message, type = "success", onClose }) => {
  const bgColor = type === "success" ? "bg-emerald-500" : type === "error" ? "bg-red-500" : "bg-blue-500"
  const icon = type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />

  return (
    <div
      className={`fixed top-4 right-4 z-50 flex items-center p-4 mb-4 rounded-lg shadow-lg ${bgColor} text-white max-w-xs animate-fade-in-right`}
    >
      <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 mr-2">{icon}</div>
      <div className="text-sm font-normal">{message}</div>
      <button
        type="button"
        onClick={onClose}
        className="ml-auto -mx-1.5 -my-1.5 rounded-lg p-1.5 inline-flex h-8 w-8 hover:bg-white hover:bg-opacity-20"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  )
}

// Tooltip component
export const Tooltip = ({ children, text }) => {
  return (
    <div className="relative group">
      {children}
      <div className="absolute z-10 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition bg-gray-900 text-white text-xs rounded py-1 px-2 -bottom-8 left-1/2 transform -translate-x-1/2 w-max">
        {text}
      </div>
    </div>
  )
}

// Description with "View Full" functionality
export const TruncatedDescription = ({ text, maxLength = 120 }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!text) return <p className="text-gray-500 italic">No description provided.</p>

  if (text.length <= maxLength) return <p className="text-gray-600 whitespace-pre-wrap">{text}</p>

  return (
    <div className="space-y-2">
      <p className="text-gray-600 whitespace-pre-wrap">{isExpanded ? text : `${text.substring(0, maxLength)}...`}</p>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors flex items-center"
      >
        {isExpanded ? (
          <>
            View Less <ChevronUp className="ml-1 h-4 w-4" />
          </>
        ) : (
          <>
            View Full Description <ChevronDown className="ml-1 h-4 w-4" />
          </>
        )}
      </button>
    </div>
  )
}

// Helper function to get content icon
export const getContentIcon = (type) => {
  const { Film, FileText, FileQuestion, Play } = require("lucide-react")

  switch (type) {
    case "video":
      return <Film size={16} className="text-red-600" />
    case "pdf":
      return <FileText size={16} className="text-blue-600" />
    case "quiz":
      return <FileQuestion size={16} className="text-purple-600" />
    default:
      return <Play size={16} className="text-gray-600" />
  }
}

// Extract YouTube video ID from URL
export const extractYoutubeVideoId = (url) => {
  if (!url) return null

  // Handle youtube.com/watch?v= format
  let match = url.match(/youtube\.com\/watch\?v=([^&]+)/)
  if (match && match[1]) return match[1].split("&")[0]

  // Handle youtu.be/ format
  match = url.match(/youtu\.be\/([^?]+)/)
  if (match && match[1]) return match[1].split("?")[0]

  return null
}
